import { useState, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { DashboardView } from './components/DashboardView';
import { RestaurantsView } from './components/RestaurantsView';
import { SupportView } from './components/SupportView';
import { TasksView } from './components/TasksView';
import { PlansView } from './components/PlansView';
import { AiCopilotView } from './components/AiCopilotView';
import { LogsView } from './components/LogsView';
import { TeamManagementView } from './components/TeamManagementView';
import { TeamWorkloadView } from './components/TeamWorkloadView';
import { CreativeLoginPage } from './components/CreativeLoginPage';
import { Restaurant, SupportTicket, DishTask, SystemLog, SubscriptionPlan, PlanType, Employee } from './types';
import { 
  RefreshCw, 
  LayoutDashboard, 
  QrCode, 
  Menu, 
  X, 
  WifiOff, 
  Shield, 
  Utensils, 
  LifeBuoy, 
  ClipboardList, 
  Users, 
  CreditCard, 
  Sparkles, 
  History 
} from 'lucide-react';

interface SystemMetrics {
  totalRest: number;
  activeRest: number;
  trialRest: number;
  suspendedRest: number;
  mrr: number;
  totalScans: number;
  totalOrderRevenue: number;
  avgRating: number;
  openTicketsCount: number;
}

const allTabs = [
  { id: 'summary', label: 'Mərhələlər Paneli', icon: LayoutDashboard },
  { id: 'restaurants', label: 'Restoranlar', icon: Utensils },
  { id: 'support', label: 'Müştəri Dəstəyi', icon: LifeBuoy },
  { id: 'tasks', label: 'Rəqəmsallaşdırma tapşırıqları', icon: ClipboardList },
  { id: 'workload', label: 'Komanda Yöndəmi & Reytinqi', icon: Users },
  { id: 'plans', label: 'Restoran ödənişləri', icon: CreditCard },
  { id: 'ai-copilot', label: 'Sİ Analitik', icon: Sparkles },
  { id: 'team', label: 'Komanda və İcazələr', icon: Shield },
  { id: 'logs', label: 'Sistem auditi', icon: History }
];

export default function App() {
  const [loggedInId, setLoggedInId] = useState<string | null>(() => {
    return localStorage.getItem('media_cube_session');
  });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorState, setErrorState] = useState<string | null>(null);

  // Operational State
  const [metrics, setMetrics] = useState<SystemMetrics>({
    totalRest: 0,
    activeRest: 0,
    trialRest: 0,
    suspendedRest: 0,
    mrr: 0,
    totalScans: 0,
    totalOrderRevenue: 0,
    avgRating: 5.0,
    openTicketsCount: 0,
  });

  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [tasks, setTasks] = useState<DishTask[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [simulatedEmployeeId, setSimulatedEmployeeId] = useState<string>(() => {
    return localStorage.getItem('media_cube_session') || 'admin';
  });

  // Comprehensive Data Synchronizer
  const fetchAllData = async () => {
    try {
      const [metricsRes, restRes, tiksRes, tasksRes, plansRes, logsRes, empsRes] = await Promise.all([
        fetch('/api/metrics'),
        fetch('/api/restaurants'),
        fetch('/api/tickets'),
        fetch('/api/tasks'),
        fetch('/api/plans'),
        fetch('/api/logs'),
        fetch('/api/employees'),
      ]);

      if (!metricsRes.ok || !restRes.ok || !tiksRes.ok || !tasksRes.ok || !plansRes.ok || !logsRes.ok || !empsRes.ok) {
        throw new Error('Xəta! Əməliyyat məlumatlarını backend serverdən yükləmək mümkün olmadı.');
      }

      const [metricsData, restData, tiksData, tasksData, plansData, logsData, empsData] = await Promise.all([
        metricsRes.json(),
        restRes.json(),
        tiksRes.json(),
        tasksRes.json(),
        plansRes.json(),
        logsRes.json(),
        empsRes.json(),
      ]);

      setMetrics(metricsData);
      setRestaurants(restData);
      setTickets(tiksData);
      setTasks(tasksData);
      setPlans(plansData);
      setLogs(logsData);
      setEmployees(empsData);
      setErrorState(null);
    } catch (err: any) {
      console.error(err);
      setErrorState(err?.message || 'Serverlə əlaqə xətası.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // API Callbacks triggered by sub-view actions

  const handleAddRestaurant = async (newRestData: any) => {
    try {
      const res = await fetch('/api/restaurants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRestData),
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to add restaurant', err);
    }
  };

  const handleUpdateRestaurant = async (id: string, updatedFields: Partial<Restaurant>) => {
    try {
      const res = await fetch(`/api/restaurants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedFields),
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to update restaurant', err);
    }
  };

  const handleDeleteRestaurant = async (id: string) => {
    try {
      const res = await fetch(`/api/restaurants/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to delete restaurant', err);
    }
  };

  const handleResolveTicket = async (id: string) => {
    try {
      const res = await fetch(`/api/tickets/${id}/resolve`, {
        method: 'POST',
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to resolve ticket', err);
    }
  };

  const handleSendTicketReply = async (id: string, message: string) => {
    try {
      const res = await fetch(`/api/tickets/${id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to reply to ticket', err);
    }
  };

  const handleAddTask = async (newTaskData: { dishName: string; restaurantId: string; executor: string; notes?: string; photo_url?: string }) => {
    try {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTaskData),
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to add production task', err);
    }
  };

  const handleUpdateTask = async (id: string, updatedFields: Partial<DishTask>) => {
    try {
      const res = await fetch(`/api/tasks/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedFields),
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to update production task', err);
    }
  };

  const handleDeleteTask = async (id: string) => {
    try {
      const res = await fetch(`/api/tasks/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to delete production task', err);
    }
  };

  const handleUpdatePlan = async (id: PlanType, data: { price: number; maxMenuItems: number; maxScansPerMonth: number }) => {
    try {
      const res = await fetch(`/api/plans/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to update plan configurations', err);
    }
  };

  const handleCreateEmployee = async (newEmp: { name: string; role: string; email: string; phone: string; visibleTabs: string[]; password?: string }) => {
    try {
      const res = await fetch('/api/employees', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEmp),
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to register employee', err);
    }
  };

  const handleUpdateEmployee = async (id: string, updatedFields: Partial<Employee>) => {
    try {
      const res = await fetch(`/api/employees/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedFields),
      });
      if (res.ok) {
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to modify employee records', err);
    }
  };

  const handleDeleteEmployee = async (id: string) => {
    try {
      const res = await fetch(`/api/employees/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        if (simulatedEmployeeId === id) {
          setSimulatedEmployeeId('admin');
        }
        await fetchAllData();
      }
    } catch (err) {
      console.error('Failed to remove employee', err);
    }
  };

  const handleLoginSuccess = (id: string) => {
    setLoggedInId(id);
    setSimulatedEmployeeId(id);
    localStorage.setItem('media_cube_session', id);
  };

  const handleLogout = () => {
    setLoggedInId(null);
    setSimulatedEmployeeId('admin');
    localStorage.removeItem('media_cube_session');
  };

  // Live Access-Control Synchronization
  useEffect(() => {
    if (simulatedEmployeeId !== 'admin') {
      const emp = employees.find(e => e.id === simulatedEmployeeId);
      if (emp) {
        const allowed = emp.visibleTabs ? emp.visibleTabs.map(t => t === 'summary' ? 'dashboard' : t) : [];
        if (!allowed.includes(activeTab)) {
          const fallback = allowed[0] || 'dashboard';
          setActiveTab(fallback);
        }
      }
    }
  }, [simulatedEmployeeId, employees]);

  // Switch Render View depending on active state tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardView metrics={metrics} restaurants={restaurants} setActiveTab={setActiveTab} />;
      case 'restaurants':
        return (
          <RestaurantsView 
            restaurants={restaurants} 
            plansArr={plans}
            onAdd={handleAddRestaurant}
            onUpdate={handleUpdateRestaurant}
            onDelete={handleDeleteRestaurant}
          />
        );
      case 'support':
        return (
          <SupportView 
            tickets={tickets} 
            onResolve={handleResolveTicket} 
            onSendReply={handleSendTicketReply}
          />
        );
      case 'tasks':
        return (
          <TasksView 
            tasks={tasks} 
            restaurants={restaurants} 
            onAddTask={handleAddTask} 
            onUpdateTask={handleUpdateTask} 
            onDeleteTask={handleDeleteTask} 
          />
        );
      case 'workload':
        return (
          <TeamWorkloadView
            tasks={tasks}
            restaurants={restaurants}
            employees={employees}
            onUpdateTask={handleUpdateTask}
          />
        );
      case 'plans':
        return (
          <PlansView 
            restaurants={restaurants} 
            onUpdateRestaurant={handleUpdateRestaurant} 
          />
        );
      case 'team':
        return (
          <TeamManagementView
            employees={employees}
            onCreateEmployee={handleCreateEmployee}
            onUpdateEmployee={handleUpdateEmployee}
            onDeleteEmployee={handleDeleteEmployee}
            simulatedEmployeeId={simulatedEmployeeId}
            onSetSimulatedEmployeeId={setSimulatedEmployeeId}
            allTabs={allTabs}
          />
        );
      case 'ai-copilot':
        return <AiCopilotView />;
      case 'logs':
        return <LogsView logs={logs} onRefresh={fetchAllData} />;
      default:
        return <DashboardView metrics={metrics} restaurants={restaurants} setActiveTab={setActiveTab} />;
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-slate-50 gap-4">
        <RefreshCw className="w-8 h-8 text-indigo-650 animate-spin" />
        <div className="text-center">
          <p className="text-sm font-bold text-slate-800">Əsas CRM serverinin işə salınması...</p>
          <p className="text-xs text-slate-400 mt-1 font-mono">Express keş yaddaşına qoşulma və Sİ modullarının tənzimlənməsi</p>
        </div>
      </div>
    );
  }

  if (errorState) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-slate-50 p-6">
        <div className="bg-white p-6 max-w-md rounded-2xl border border-red-200 text-center space-y-4 shadow-xl">
          <WifiOff className="w-12 h-12 text-rose-500 mx-auto" />
          <h2 className="font-display font-extrabold text-lg text-slate-900">Backend server əlçatmazdır</h2>
          <p className="text-xs text-slate-500 leading-relaxed">
            {errorState}. Zəhmət olmasa Express serverinin işlək vəziyyətdə olduğundan və port 3000-in aktivliyindən əmin olun.
          </p>
          <button
            type="button"
            onClick={() => {
              setIsLoading(true);
              fetchAllData();
            }}
            className="w-full py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-all"
          >
            Yenidən sinxronizasiya etməyə çalış
          </button>
        </div>
      </div>
    );
  }

  const getSidebarAllowedTabs = () => {
    if (simulatedEmployeeId === 'admin') return undefined;
    const emp = employees.find(e => e.id === simulatedEmployeeId);
    return emp ? emp.visibleTabs : [];
  };

  if (!loggedInId) {
    return <CreativeLoginPage employees={employees} onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="min-h-screen bg-slate-50 flex overflow-hidden">
      
      {/* Desktop Persistent Sidebar */}
      <div className="hidden lg:block shrink-0">
        <Sidebar 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          openTicketsCount={metrics.openTicketsCount} 
          allowedTabs={getSidebarAllowedTabs()}
        />
      </div>

      {/* Floating Mobile Sidebar Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden flex">
          <div className="relative z-50 flex-1">
            <Sidebar 
              activeTab={activeTab} 
              setActiveTab={(tab) => {
                setActiveTab(tab);
                setMobileMenuOpen(false);
              }} 
              openTicketsCount={metrics.openTicketsCount} 
              allowedTabs={getSidebarAllowedTabs()}
            />
          </div>
          <div 
            onClick={() => setMobileMenuOpen(false)}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs" 
          />
        </div>
      )}

      {/* Main Right Side Content Container */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        
        {/* Upper global header/bar */}
        <header className="bg-white border-b border-slate-200 h-16 shrink-0 flex items-center justify-between px-6 shadow-xs">
          <div className="flex items-center gap-3">
            {/* Mobile menu trigger */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1.5 focus:bg-slate-100 rounded-lg lg:hidden text-slate-700"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            {/* Secondary breadcrumb metadata */}
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase font-mono tracking-wider font-extrabold text-slate-400">
                Cari Bölmə
              </span>
              <span className="text-slate-300">/</span>
              <span className="text-xs font-semibold text-indigo-700 capitalize font-mono bg-indigo-50 px-2 py-0.5 rounded">
                {activeTab === 'ai-copilot' ? 'Sİ Biznes Köməkçisi' : 
                 activeTab === 'tasks' ? 'Rəqəmsallaşdırma' : 
                 activeTab === 'workload' ? 'Komanda Yöndəmi & Reytinqi' : 
                 activeTab === 'plans' ? 'Restoran Ödənişləri' : 
                 activeTab === 'support' ? 'Müştəri Dəstəyi' : 
                 activeTab === 'restaurants' ? 'Restoranlar' : 'Xülasə'}
              </span>
            </div>
          </div>

          {/* Quick Realtime Synchronizer triggers */}
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={() => {
                setIsLoading(true);
                fetchAllData();
              }}
              className="p-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-all text-slate-500 hover:text-slate-800 flex items-center gap-1.5 text-xs font-semibold cursor-pointer"
              title="Məlumatları sinxronizasiya et"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Yenilə</span>
            </button>

            {/* Logout trigger button */}
            <button
              type="button"
              onClick={handleLogout}
              className="p-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 hover:text-rose-700 border border-rose-200/60 rounded-lg transition-all flex items-center gap-1.5 text-xs font-bold cursor-pointer select-none"
              title="Sistemdən çıx"
            >
              <X className="w-3.5 h-3.5 shrink-0" />
              <span className="max-w-[75px] sm:max-w-none truncate">
                Çıxış ({loggedInId === 'admin' ? 'Admin' : employees.find(e => e.id === loggedInId)?.avatar || 'Əməkdaş'})
              </span>
            </button>
          </div>
        </header>

        {/* Dynamic Inner View Box scroll area */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          <div className="max-w-7xl mx-auto pb-12">
            {renderTabContent()}
          </div>
        </main>

      </div>

    </div>
  );
}
